import { useRef, useState } from "react";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSMenuItem from "../../system-design/DSMenuItem";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import KlydosByIdsTable from "./KlydosByIdsTable";
import useFirmwareKlydosQuery from "../hooks/useFirmwareKlydosQuery";
import useCreateFirmwareKlydosMutation from "../hooks/useCreateFirmwareKlydosMutation";
import { FirmwareKlydos } from "../../../api/firmware-klydos";

type Source = "existing" | "scratch";
type Step = "choose" | "pick" | "confirm";

const firmwareLabel = (fw: FirmwareKlydos): string =>
  fw.name?.trim() ? fw.name : fw.id;

type CreateFirmwareKlydosModalProps = {
  onClose: () => void;
  onCreated?: (created: FirmwareKlydos) => void;
};

const CreateFirmwareKlydosModal = ({
  onClose,
  onCreated,
}: CreateFirmwareKlydosModalProps) => {
  const { data: firmwares = [] } = useFirmwareKlydosQuery();
  const createMutation = useCreateFirmwareKlydosMutation();

  const [step, setStep] = useState<Step>("choose");
  const [source, setSource] = useState<Source>("scratch");
  // The firmware whose klydos were inherited into the picker (existing flow).
  // The list arrives sorted most-recent-first, so index 0 is the default.
  const [inheritFirmwareId, setInheritFirmwareId] = useState<string>("");
  // The single source of truth for the current selection. Preserved when
  // moving between the pick and confirm steps.
  const [selection, setSelection] = useState<string[]>([]);

  // KlydosPickDialog.handleSave calls onPickKlydos AND then onClose. Here save
  // must advance to the confirm step, not tear down the whole flow — so this
  // flag lets the picker's onClose tell a save-close apart from a cancel-close.
  const justPickedRef = useRef(false);

  const klydosOf = (firmwareId: string): string[] =>
    firmwares.find((fw) => fw.id === firmwareId)?.klydosIds ?? [];

  const startExisting = () => {
    const firstId = firmwares[0]?.id ?? "";
    setSource("existing");
    setInheritFirmwareId(firstId);
    setSelection(klydosOf(firstId));
    setStep("pick");
  };

  const startScratch = () => {
    setSource("scratch");
    setSelection([]);
    setStep("pick");
  };

  // Switching the inherited firmware re-seeds the selection from its klydos
  // (i.e. "inherit all klydos from it").
  const handleInheritChange = (firmwareId: string) => {
    setInheritFirmwareId(firmwareId);
    setSelection(klydosOf(firmwareId));
  };

  const handlePicked = (ids: string[]) => {
    justPickedRef.current = true;
    setSelection(ids);
    setStep("confirm");
  };

  // The picker fires onClose both on cancel and right after a save. Only the
  // cancel path should close the whole create flow.
  const handlePickerClose = () => {
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return;
    }
    onClose();
  };

  const handleConfirmCreate = async () => {
    const created = await createMutation.mutateAsync({ klydosIds: selection });
    onCreated?.(created);
    onClose();
  };

  if (step === "choose") {
    return (
      <DSDialog open fullWidth maxWidth="sm" onClose={onClose}>
        <DSDialogTitle>Create new firmware klydos</DSDialogTitle>
        <DSDialogContent dividers>
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}
          >
            <DSPrimaryButton
              onClick={startExisting}
              disabled={firmwares.length === 0}
              title={
                firmwares.length === 0
                  ? "No existing firmwares to inherit from yet"
                  : undefined
              }
            >
              Create from existing firmware
            </DSPrimaryButton>
            <DSSecondaryButton onClick={startScratch}>
              Create from scratch
            </DSSecondaryButton>
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={onClose}>Cancel</DSSecondaryButton>
        </DSDialogActions>
      </DSDialog>
    );
  }

  if (step === "pick") {
    const headerRight =
      source === "existing" ? (
        <DSBox display="flex" alignItems="center" gap={1}>
          <DSTypography variant="caption" sx={{ whiteSpace: "nowrap" }}>
            Inherit from:
          </DSTypography>
          <DSTextField
            select
            size="small"
            sx={{ minWidth: 260 }}
            value={inheritFirmwareId}
            onChange={(e) => handleInheritChange(e.target.value)}
          >
            {firmwares.map((fw) => (
              <DSMenuItem key={fw.id} value={fw.id}>
                {firmwareLabel(fw)}
              </DSMenuItem>
            ))}
          </DSTextField>
        </DSBox>
      ) : undefined;

    return (
      <KlydosPickDialog
        // Re-key on the inherited firmware so switching it remounts the picker
        // seeded from the (freshly re-inherited) selection. Coming back from the
        // confirm step keeps the same key, so edits are preserved.
        key={`${source}-${inheritFirmwareId}`}
        title="Select klydos"
        headerRight={headerRight}
        saveLabel="Review selection"
        selectedKlydos={selection}
        onPickKlydos={handlePicked}
        onClose={handlePickerClose}
      />
    );
  }

  // step === "confirm"
  return (
    <DSDialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DSDialogTitle>
        Confirm selection — {selection.length} klydo
        {selection.length === 1 ? "" : "s"}
      </DSDialogTitle>
      <DSDialogContent
        dividers
        style={{ display: "flex", flexDirection: "column", height: "70vh" }}
      >
        <DSTypography variant="body2" sx={{ mb: 2 }} color="text.secondary">
          These klydos will be saved to a new (unnamed) firmware. The name can
          be set later directly in MongoDB.
        </DSTypography>
        {/* The table measures its own height from the flex chain, so it needs a
            definite-height flex parent here (minHeight:0 lets it shrink). */}
        <DSBox
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <KlydosByIdsTable
            klydosIds={selection}
            tableId="firmware-klydos-confirm"
          />
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton
          onClick={() => setStep("pick")}
          disabled={createMutation.isPending}
        >
          Back
        </DSSecondaryButton>
        <DSPrimaryButton
          onClick={handleConfirmCreate}
          disabled={createMutation.isPending || selection.length === 0}
        >
          {createMutation.isPending ? "Creating…" : "Confirm & Create"}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default CreateFirmwareKlydosModal;
